/**
 * Reiter „Zusagen": was du Kunden versprochen hast, je Kunde als Checkliste,
 * plus deine letzten Notizen mit der Zuordnung des Laufs (Passt / Ändern).
 */

import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Check } from 'lucide-react';
import { listZusagen, patchZusage, neueZusage, listNotizen, bestaetigeNotiz, zahl, type Zusage, type Notiz, type NotizFolge, type KundeZeile } from '@/lib/leitwarte-api';
import { Karte, Kopf, Laden, Leer, Fehlerzeile, T, kurzDatum, kurzDatumZeit, mehrzahl } from './bausteine';

const NOTIZ_STATUS: Record<Notiz['status'], string> = {
  neu: 'neu, noch nicht gelesen',
  zugeordnet: 'zugeordnet, wartet auf dein OK',
  bestaetigt: 'bestätigt',
  verworfen: 'verworfen',
};
const FOLGE_ART: Record<NotizFolge['art'], string> = {
  zusage_neu: 'Neue Zusage',
  zusage_status: 'Zusage geändert',
  vorgang_neu: 'Neuer Vorgang',
  auftrag: 'Auftrag an Skill, wird nach OK ausgeführt',
  hinweis: 'Hinweis',
};

interface Gruppe { kunde: string; untergruppen: Array<{ name: string | null; zusagen: Zusage[] }> }

/** Gruppiert nach Kunde, darin nach „gruppe" (Reihenfolge des Endpunkts bleibt, Erledigtes rutscht ans Ende). */
function gruppiere(zusagen: Zusage[]): Gruppe[] {
  const kunden = new Map<string, Map<string | null, Zusage[]>>();
  for (const z of zusagen) {
    const k = z.kunde_name ?? 'Ohne Kunde';
    if (!kunden.has(k)) kunden.set(k, new Map());
    const u = kunden.get(k)!;
    const g = z.gruppe ?? null;
    if (!u.has(g)) u.set(g, []);
    u.get(g)!.push(z);
  }
  const rang = (z: Zusage) => (z.status === 'erledigt' || z.status === 'verworfen' ? 1 : 0);
  return [...kunden.entries()].map(([kunde, u]) => ({
    kunde,
    untergruppen: [...u.entries()].map(([name, liste]) => ({ name, zusagen: [...liste].sort((a, b) => rang(a) - rang(b)) })),
  }));
}

function ZusageZeile({ z, umschalten, laeuft }: { z: Zusage; umschalten: () => void; laeuft: boolean }) {
  const erledigt = z.status === 'erledigt';
  const verworfen = z.status === 'verworfen';
  return (
    <div className="lw-zeile" style={{ padding: '5px 0', gap: 10 }}>
      <button type="button" className={`lw-kasten${erledigt ? ' lw-kasten-an' : ''}`} disabled={laeuft || verworfen} onClick={umschalten} title={erledigt ? 'Wieder öffnen' : 'Als erledigt markieren'} aria-label={erledigt ? 'Wieder öffnen' : 'Als erledigt markieren'}>
        {erledigt ? <Check size={11} strokeWidth={3} /> : null}
      </button>
      <span style={{ flexGrow: 1, minWidth: 0 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: erledigt || verworfen ? T.ged : T.text, textDecoration: erledigt || verworfen ? 'line-through' : 'none' }} title={z.beschreibung ?? undefined}>{z.titel}</span>
        {z.bedingung ? <span className="lw-12 lw-ged"> · wartet auf {z.bedingung}</span> : null}
        {!z.bedingung && z.status === 'wartet' ? <span className="lw-12 lw-ged"> · wartet</span> : null}
      </span>
      {z.faellig_am ? <span className="lw-12 lw-sek" style={{ whiteSpace: 'nowrap' }}>bis {kurzDatum(z.faellig_am)}</span> : null}
      {z.quelle ? <span className="lw-chip">{z.quelle}</span> : null}
    </div>
  );
}

export default function Zusagen({ kunden }: { kunden: KundeZeile[] }) {
  const qc = useQueryClient();
  const zusagen = useQuery({ queryKey: ['leitwarte', 'zusagen', 'alle'], queryFn: () => listZusagen('alle') });
  const notizen = useQuery({ queryKey: ['leitwarte', 'notizen'], queryFn: listNotizen });

  const [titel, setTitel] = useState('');
  const [kundeId, setKundeId] = useState('');
  const [bedingung, setBedingung] = useState('');
  const [faellig, setFaellig] = useState('');

  const umschalten = useMutation({
    mutationFn: (z: Zusage) => patchZusage(z.id, { status: z.status === 'erledigt' ? 'offen' : 'erledigt' }),
    onSuccess: (z) => { qc.invalidateQueries({ queryKey: ['leitwarte'] }); toast.success(z.status === 'erledigt' ? 'Zusage erledigt' : 'Zusage wieder offen'); },
    onError: (e: Error) => toast.error(e.message || 'Das hat nicht geklappt'),
  });
  const anlegen = useMutation({
    mutationFn: () => neueZusage({ titel: titel.trim(), customer_id: kundeId || null, bedingung: bedingung.trim() || null, faellig_am: faellig || null, status: bedingung.trim() ? 'wartet' : 'offen', quelle: 'web' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['leitwarte'] }); toast.success('Zusage angelegt'); setTitel(''); setKundeId(''); setBedingung(''); setFaellig(''); },
    onError: (e: Error) => toast.error(e.message || 'Zusage konnte nicht angelegt werden'),
  });
  const bestaetigen = useMutation({
    mutationFn: (p: { id: string; ok: boolean }) => bestaetigeNotiz(p.id, p.ok),
    onSuccess: (_n, p) => { qc.invalidateQueries({ queryKey: ['leitwarte'] }); toast.success(p.ok ? 'Zuordnung bestätigt' : 'Zuordnung verworfen, der Lauf schaut noch einmal'); },
    onError: (e: Error) => toast.error(e.message || 'Das hat nicht geklappt'),
  });

  const absenden = (e: FormEvent) => {
    e.preventDefault();
    if (!titel.trim() || anlegen.isPending) return;
    anlegen.mutate();
  };

  const gruppen = gruppiere(zusagen.data ?? []);
  const offen = (zusagen.data ?? []).filter((z) => z.status === 'offen' || z.status === 'wartet').length;
  const letzteNotizen = [...(notizen.data ?? [])].sort((a, b) => (b.created_at > a.created_at ? 1 : -1)).slice(0, 10);

  return (
    <div className="lw-grid lw-g-inhalt" style={{ alignItems: 'start' }}>
      <Karte style={{ padding: '20px 24px', gap: 12 }}>
        <Kopf titel="Zusagen" sub={zusagen.data ? `${zahl(offen)} ${mehrzahl(offen, 'offene Zusage', 'offene Zusagen')} an Kunden` : undefined} />
        {!zusagen.data ? (
          zusagen.isLoading ? <Laden text="Lade die Zusagen …" /> : <Fehlerzeile error={zusagen.error} retry={() => zusagen.refetch()} />
        ) : gruppen.length === 0 ? (
          <Leer text="Keine Zusagen erfasst. Sprich eine Notiz ein oder lege unten eine an." />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {gruppen.map((g) => (
              <div key={g.kunde} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: 13, fontWeight: 700 }}>{g.kunde}</span>
                {g.untergruppen.map((u) => (
                  <div key={u.name ?? '_'} style={{ display: 'flex', flexDirection: 'column', paddingLeft: u.name ? 10 : 0, borderLeft: u.name ? `2px solid ${T.rand}` : undefined }}>
                    {u.name ? <span className="lw-11 lw-ged" style={{ fontWeight: 600, paddingTop: 4 }}>{u.name}</span> : null}
                    {u.zusagen.map((z) => (
                      <ZusageZeile key={z.id} z={z} umschalten={() => umschalten.mutate(z)} laeuft={umschalten.isPending && umschalten.variables?.id === z.id} />
                    ))}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </Karte>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
        <Karte>
          <Kopf titel="Neue Zusage" />
          <form onSubmit={absenden} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <input className="lw-eingabe" placeholder="Was hast du zugesagt?" value={titel} onChange={(e) => setTitel(e.target.value)} maxLength={200} />
            <select className="lw-eingabe" value={kundeId} onChange={(e) => setKundeId(e.target.value)} aria-label="Kunde">
              <option value="">Ohne Kunde</option>
              {kunden.map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}
            </select>
            <input className="lw-eingabe" placeholder="Wartet auf … (optional)" value={bedingung} onChange={(e) => setBedingung(e.target.value)} maxLength={200} />
            <div className="lw-zeile" style={{ gap: 8 }}>
              <input type="date" className="lw-eingabe" value={faellig} onChange={(e) => setFaellig(e.target.value)} aria-label="Fällig am" style={{ flexGrow: 1 }} />
              <button type="submit" className="lw-knopf lw-knopf-primaer" style={{ height: 36 }} disabled={!titel.trim() || anlegen.isPending}>Anlegen</button>
            </div>
          </form>
        </Karte>

        <Karte>
          <Kopf titel="Notizen" sub="die letzten zehn, mit der Zuordnung des Laufs" />
          {!notizen.data ? (
            notizen.isLoading ? <Laden text="Lade die Notizen …" /> : <Fehlerzeile error={notizen.error} retry={() => notizen.refetch()} />
          ) : letzteNotizen.length === 0 ? (
            <Leer text="Noch keine Notizen. Oben in der Leiste sprechen oder tippen." />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 12 }}>
              {letzteNotizen.map((n) => (
                <div key={n.id} className="lw-innen" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span style={{ fontStyle: 'italic', color: T.sek }}>„{n.text}“</span>
                  <span className="lw-11 lw-ged">
                    {kurzDatumZeit(n.created_at)} · {NOTIZ_STATUS[n.status] ?? n.status}
                    {n.zuordnung?.kunde_name ? ` · ${n.zuordnung.kunde_name}` : ''}
                  </span>
                  {n.zuordnung?.zusammenfassung ? <span className="lw-sek">{n.zuordnung.zusammenfassung}</span> : null}
                  {(n.folgen ?? []).map((f, i) => (
                    <span key={i} className="lw-zeile lw-sek" style={{ gap: 6, alignItems: 'flex-start' }}>
                      <span className="lw-chip" style={{ flexShrink: 0 }}>{FOLGE_ART[f.art] ?? f.art}</span>
                      <span style={{ minWidth: 0 }}>{f.text}</span>
                      {f.angewendet ? <Check size={12} color={T.gut} style={{ flexShrink: 0, marginTop: 3 }} /> : null}
                    </span>
                  ))}
                  {n.status === 'zugeordnet' ? (
                    <div className="lw-zeile" style={{ gap: 8, marginTop: 2 }}>
                      <button type="button" className="lw-knopf lw-knopf-klein lw-knopf-primaer" disabled={bestaetigen.isPending} onClick={() => bestaetigen.mutate({ id: n.id, ok: true })}>Passt</button>
                      <button type="button" className="lw-knopf lw-knopf-klein" disabled={bestaetigen.isPending} onClick={() => bestaetigen.mutate({ id: n.id, ok: false })}>Ändern</button>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </Karte>
      </div>
    </div>
  );
}
